import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

type PartnerQuoteMedia = {
  src?: string
  alt?: string
}

type PartnerQuoteLogo = {
  src?: string
  alt?: string
  label?: string
}

type PartnerQuoteProps = {
  quote: string
  cite: string
  variant?: 'feature' | 'article' | 'profile' | 'case'
  media?: PartnerQuoteMedia
  logo?: PartnerQuoteLogo
  citeTo?: string
  action?: ReactNode
  ariaLabel?: string
}

export const PartnerQuote = ({ quote, cite, variant = 'article', media, logo, citeTo, action, ariaLabel }: PartnerQuoteProps) => {
  const className = ['partner-quote', `partner-quote--${variant}`].join(' ')
  const hasMedia = Boolean(media?.src)
  const hasPrimaryMedia = hasMedia && (variant === 'feature' || variant === 'case')
  const hasCaptionAvatar = hasMedia && (variant === 'article' || variant === 'profile')
  const hasLogo = variant !== 'case' && Boolean(logo?.src)
  const caseCite = variant === 'case' && logo?.label && !cite.includes(logo.label) ? `${cite} of ${logo.label}` : cite
  const formattedCite = variant === 'profile' ? caseCite.replace(', ', ',\n') : caseCite
  const Root = variant === 'feature' ? 'section' : 'figure'
  const Caption = variant === 'feature' ? 'footer' : 'figcaption'
  const quoteElement = (
    <blockquote>
      <p>{quote}</p>
    </blockquote>
  )
  const captionElement = (
    <Caption className={hasCaptionAvatar ? 'partner-quote__caption partner-quote__caption--with-avatar' : 'partner-quote__caption'}>
      {hasCaptionAvatar ? <img className="partner-quote__avatar" src={media?.src} alt={media?.alt ?? ''} loading="lazy" /> : null}
      {variant === 'profile' && !hasMedia ? <span className="partner-quote__avatar" aria-hidden /> : null}
      {citeTo ? (
        <Link className="partner-quote__cite-link" to={citeTo}>{formattedCite}</Link>
      ) : (
        <span>{formattedCite}</span>
      )}
      {hasLogo ? <img className="partner-quote__logo" src={logo?.src} alt={logo?.alt || logo?.label || ''} loading="lazy" /> : null}
    </Caption>
  )
  const attributionElement = action ? (
    <div className="partner-quote__attribution">
      {captionElement}
      {action}
    </div>
  ) : captionElement

  return (
    <Root className={className} aria-label={ariaLabel}>
      {hasPrimaryMedia && (variant === 'feature' || variant === 'case') ? (
        <div className="partner-quote__media-frame">
          <img className="partner-quote__media" src={media?.src} alt={media?.alt ?? ''} loading="lazy" decoding="async" />
        </div>
      ) : hasPrimaryMedia ? (
        <img className="partner-quote__media" src={media?.src} alt={media?.alt ?? ''} loading="lazy" decoding="async" />
      ) : variant === 'case' ? (
        <div className="partner-quote__media-placeholder" aria-hidden />
      ) : null}
      {variant === 'feature' || variant === 'case' ? (
        <div className="partner-quote__content">
          {quoteElement}
          {attributionElement}
        </div>
      ) : (
        <>
          {quoteElement}
          {attributionElement}
        </>
      )}
    </Root>
  )
}
